import {ResearchLitElement, css} from '@core/research-lit-element';
import {BdCookieConsent} from '@bog-design-system/bd-cookie-consent';
import '@bog-design-system/bd-font';
import '@bog-design-system/bd-toggle';
import '@bog-design-system/bd-link';
import {GoogleAnalytics} from './google-analytics';
import {FbPixel} from './FbPixel';
import {html} from 'lit';

export class ResearchUserConsent extends ResearchLitElement {
    static get is() {
        return 'research-user-consent';
    }

    static get styles() {
        return css`
        `;
    }

    static get properties() {
        return {
            data: {
                type: Object,
            },
        };
    };

    renderFirstPageDescription = () => html`
        <bd-font type="body2">
            ${this.data?.firstPageDescription}
            <bd-link href="${this.data?.detailsButtonUrl?.link}" target="_blank" type="primary" icon="bd:external"
                >${this.data?.detailsButton}
            </bd-link>
        </bd-font>
    `;

    renderSlotContent = () => html`
        <bd-cookie-consent-item>
            <bd-font slot="title" type="title">${this.data?.detailsTitle}</bd-font>
            <div slot="description" style="display: flex; flex-direction: column;">
                <bd-font type="body2"
                    >${this.data?.detailsDescription}
                </bd-font>
            </div>
        </bd-cookie-consent-item>

        ${this.data?.toggles?.map((item, index) => html`
          <bd-cookie-consent-item id="${item.id}" ?border=${index === 0}>
              <bd-font slot="title" type="subtitle">${item?.title}</bd-font>
              <bd-font slot="description" type="body2">
                ${item?.description}
              </bd-font>
              <bd-toggle
                slot="toggle"
                ?checked="${index === 0}"
                ?disabled=${index === 0}
                @checked-changed="${(e) => this.permissionChanged(item?.id, e.detail.value)}"
              ></bd-toggle>
          </bd-cookie-consent-item>
        `)}
    `;

    constructor() {
        super();

        this.consents = [];
    }

    async connectedCallback() {
        super.connectedCallback();
        this.apiCall(`common/get-by-content-type?contentType=App.Common.CookieConsent`)
            .then((res) => {
                this.data = res.json.data;
                this.checkUserConsent();
            })
            .catch((err) => {
                console.error('Error fetching cookie consent data:', err);
            });
    }


    _isFacebookInApp() {
        return /FBAN|FBAV|FB_IAB/i.test(navigator.userAgent || '');
    }

    _adjustCookieBannerForFb(offsetPx = 60) {
        if (!this._isFacebookInApp()) return;

        const applyOffset = (el) => {
            if (el) el.style.bottom = `${offsetPx}px`;
        };

        applyOffset(document.querySelector('bd-cookie-consent'));

        const obs = new MutationObserver(() => {
            const el = document.querySelector('bd-cookie-consent');
            if (el) {
                applyOffset(el);
                obs.disconnect();
            }
        });
        obs.observe(document.documentElement, {childList: true, subtree: true});
    }

    initializeTags(consents) {
        if (consents?.analytics) {
            GoogleAnalytics.initialize();
            console.log('Google Analytics initialized');
        }
        if (consents?.advertising) {
            FbPixel.initialize();
            console.log('Facebook Pixel initialized');
        }
    }

    static arrayToMap(array) {
        return array.reduce((map, obj) => {
            map.set(obj.channel, obj);
            return map;
        }, new Map());
    }

    async checkUserConsent() {
        setTimeout(async () => {
            const globalConsent = await this.readGlobalConsent();
            this.channelConsent = this.getLocalConsents(globalConsent);
            this.consents = this.channelConsent.get(ResearchUserConsent.domain).consent;
            if (this.isConsentInvalid(this.channelConsent)) {
                BdCookieConsent.open({
                    right: true,
                    acceptAllButtonText: this.data?.acceptAllButtonText || 'Accept all',
                    secondPageTitle: this.data?.secondPageTitle || 'second page title',
                    firstPageTitle: this.data?.firstPageTitle || 'first page title',
                    firstPageDescription: this.renderFirstPageDescription || 'first page description',
                    modify: this.data?.modify || 'modify',
                    save: this.data?.save || 'save',
                    rejectAll: this.data?.rejectAll || 'reject all',
                    renders: this.renderSlotContent,
                })
                    .then((newConsent) => {
                        this.setNewConsents(
                            this.channelConsent,
                            globalConsent,
                            newConsent.closeType === 'acceptAll',
                        );
                        this.initializeTags(this.channelConsent.get(ResearchUserConsent.domain).consent);
                    })
                    .catch(() => null);
                this._adjustCookieBannerForFb(200);
            } else {
                this.initializeTags(this.channelConsent.get(ResearchUserConsent.domain).consent);
            }
        }, 2000);
    }

    getLocalConsents(globalConsent) {
        const localConsentMap = new Map();

        for (const consent of ResearchUserConsent.defaultConsent) {
            localConsentMap.set(consent.channel, globalConsent.get(consent.channel) || consent);
        }

        return localConsentMap;
    }

    isConsentInvalid(consents) {
        const validityTime = new Date().getTime() - (1000 * 60 * 60 * 24 * BdCookieConsent.validityDays);
        return Array.from(consents.values())
            .some((consent) => consent.modified < validityTime);
    }

    async readGlobalConsent() {
        try {
            const globalConsentString = await localStorage.getItem('userConsent');
            const consentRef = JSON.parse(globalConsentString);
            if (Array.isArray(consentRef) === true) {
                return ResearchUserConsent.arrayToMap(consentRef);
            }
        } catch {
            console.log('No user consent found');
        }

        return new Map();
    }

    setNewConsents(channelConsent, globalConsent, acceptAll = false) {
        const newConsent = new Map([...globalConsent]);
        /* Update modified time for channelConsent entries */
        for (const [key, value] of channelConsent) {
            if (acceptAll) {
                for (const consentKey of Object.keys(value.consent)) {
                    value.consent[consentKey] = true;
                }
            }
            value.modified = Date.now();
            newConsent.set(key, value);
        }
        /* Save new consents */
        return localStorage.setItem('userConsent', JSON.stringify(Array.from(newConsent.values())));
    }

    async permissionChanged(channel, value) {
        this.channelConsent.get(ResearchUserConsent.domain).consent[channel] = value;
    }

    static get domain() {
        return ResearchUserConsent.defaultConsent[0].channel;
    }

    static get defaultConsent() {
        return [
            {
                channel: 'research.bog.ge',
                modified: 0,
                consent: {
                    essential: true,
                    advertising: false,
                    analytics: false,
                },
            },
        ];
    }
}


window.customElements.define(ResearchUserConsent.is, ResearchUserConsent);
